/**
 * @class entityComposition.
 *
 */
import _ from 'underscore';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionRegistrar } from '../../../actions/entityActionRegistrar';
// import logger from '../../../utils/logger';
import { getIsPermittedWithAnyOfThesePermissions, getIsPermittedWithNoneOfThesePermissions } from './permissions';
const checkPermissions = perms => {
  let result = false;
  // if not overriden, check the perms property.
  if (perms.include) {
    result = getIsPermittedWithAnyOfThesePermissions(perms.include);
  }
  if (perms.exclude) {
    result = result || getIsPermittedWithNoneOfThesePermissions(perms.exclude);
  }
  return result;
};
const entityComposition = function entityComposition(composeClass, entityList) {
  let propOverrides = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let actionsOnly = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  /*  ----------------------------------------------------
   + These methods allow you to massage variables into properties passed in
   + props to the component.
   +
   + The first takes the (global) redux state as input, and outputs just the
   + relevant parts to the compoment.
   + */
  // map the redux store/state into props for this component.
  const mapStateToProps = (state, ownProps) => {
    const result = {};
    entityList.map((entity, index) => {
      result[entity] = state[entity];
      const e = state[entity];
      if (!e) {
        throw new Error("entity '".concat(entity, "' is not properly defined. Check both action and reducer registration"), e);
      }

      // check the FIRST entity for permissions
      if (index === 0) {
        if (_.isUndefined(propOverrides.canEdit) && e.permissions && e.permissions.edit) {
          result.canEdit = checkPermissions(e.permissions.edit, propOverrides);
        }
        if (_.isUndefined(propOverrides.canView) && e.permissions && e.permissions.view) {
          result.canView = checkPermissions(e.permissions.view, propOverrides);
        }
      }
      return true;
    });
    const props = Object.keys(propOverrides);

    // this allows you to override permissions or other properties specifically.
    if (props.length > 0) {
      props.map(prop => {
        // This allows you to set a function tio be evaluated with the state
        // passed in.  What it disallows is the ability to set a prop to a
        // funciton.  Not sure if that is going to be necessary in the long
        // run, but you can always do a custom composition if necessary.
        if (_.isFunction(propOverrides[prop])) {
          // invoke the function.
          result[prop] = propOverrides[prop](state, ownProps);
        } else {
          // assign the value
          result[prop] = propOverrides[prop];
        }
        return true;
      });
    }
    return result;
  };

  // map the redux dipsatch mechanism into actions prop for this component.
  const mapDispatchToProps = dispatch => {
    const result = {};
    const actionList = entityList.concat(actionsOnly);
    actionList.map(entity => {
      // const actionName = entity + 'ActionCreators';
      const actions = actionRegistrar(entity);
      if (actions) {
        const actionStr = "".concat(entity, "Actions");
        result[actionStr] = bindActionCreators(actions, dispatch);
        // console.log(`including actions for entity: ${entity} [${actionStr}]`, result[actionStr]);
      } else {
        /* eslint-disable no-console */
        console.info("Failed including actions for entity: ".concat(entity));
        /* eslint-enable no-console */
      }
      return true;
    });
    return result;
  };

  /**
   * Compares two style objects by value (deep equality for style only)
   * @param {Object} style1 - First style object
   * @param {Object} style2 - Second style object
   * @returns {boolean} True if styles are equal by value
   */
  const areStyleObjectsEqual = (style1, style2) => {
    const nextStyle = style1 || {};
    const prevStyle = style2 || {};
    const nextStyleKeys = Object.keys(nextStyle);
    const prevStyleKeys = Object.keys(prevStyle);
    if (nextStyleKeys.length !== prevStyleKeys.length) {
      return false;
    }

    // Check if all style values are equal
    return nextStyleKeys.every(key => nextStyle[key] === prevStyle[key]);
  };

  /**
   * Compares props using shallow equality, excluding style property
   * @param {Object} props1 - First props object
   * @param {Object} props2 - Second props object
   * @returns {boolean} True if props are equal (excluding style)
   */
  const areOtherPropsEqual = (props1, props2) => {
    const keys1 = Object.keys(props1);
    const keys2 = Object.keys(props2);
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check all props except style (style is checked separately)
    return keys1.every(key => {
      if (key === 'style') {
        return true; // Style checked separately
      }
      return props1[key] === props2[key];
    });
  };

  /**
   * Custom equality check for merged props to prevent unnecessary re-renders
   * when selectors return new object references with same values.
   * Style objects are compared by value, other props use shallow equality.
   * @param {Object} nextMergedProps - Next props to compare
   * @param {Object} prevMergedProps - Previous props to compare
   * @returns {boolean} True if props are equal
   */
  const areMergedPropsEqual = (nextMergedProps, prevMergedProps) => {
    // If style objects are different references, compare their values
    if (nextMergedProps.style !== prevMergedProps.style) {
      // Compare style objects by value
      if (!areStyleObjectsEqual(nextMergedProps.style, prevMergedProps.style)) {
        return false;
      }

      // Style values are equal, check other props
      return areOtherPropsEqual(nextMergedProps, prevMergedProps);
    }

    // Style references are same, use shallow equality for all props
    const nextKeys = Object.keys(nextMergedProps);
    const prevKeys = Object.keys(prevMergedProps);
    if (nextKeys.length !== prevKeys.length) {
      return false;
    }
    return nextKeys.every(key => nextMergedProps[key] === prevMergedProps[key]);
  };

  // This wraps and instance of an entity in ConnectedClass and
  // connects it to the subset of the redux state as well as the action set
  // Use custom equality check for merged props to prevent unnecessary re-renders
  // when selectors return new object references with same values
  return connect(mapStateToProps, mapDispatchToProps, null, {
    areMergedPropsEqual
  })(composeClass);
};
export default entityComposition;